import {
	ApartmentOutlined,
	BankOutlined,
	BuildOutlined,
	ClockCircleOutlined,
	DeleteOutlined,
	EditOutlined,
	HomeOutlined,
	ShoppingCartOutlined,
	TeamOutlined,
	UserOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, message, Popconfirm, Spin } from "antd";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { getAdminUsers } from "@/modules/users/services/users.backend";
import { formatOrganizationDate, organizationsApi } from "../api/organizations.api";
import { OrganizationBackLink } from "../components/OrganizationBackLink";
import { OrganizationStatusBadge } from "../components/OrganizationStatusBadge";

export function OrganizationDetailPage() {
	const { organizationId } = useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { data: rawOrganization, isLoading } = useQuery({
		queryKey: ["organization", organizationId],
		queryFn: () => {
			if (!organizationId) {
				throw new Error("Organization ID is required");
			}
			return organizationsApi.getOrganization(organizationId);
		},
		enabled: Boolean(organizationId),
	});
	const { data: organizationUsers } = useQuery({
		queryKey: ["admin-users", organizationId, "organization-detail-count"],
		queryFn: () => {
			if (!organizationId) {
				throw new Error("Organization ID is required");
			}

			return getAdminUsers({ orgId: organizationId });
		},
		enabled: Boolean(organizationId),
	});

	const toggleMutation = useMutation({
		mutationFn: () => {
			if (!organizationId) {
				throw new Error("Organization ID is required");
			}
			return organizationsApi.toggleOrganization(organizationId);
		},
		onSuccess: (updatedOrganization) => {
			message.success("Tashkilot holati o'zgartirildi");
			queryClient.invalidateQueries({ queryKey: ["organizations"] });
			queryClient.setQueryData(["organization", updatedOrganization.id], updatedOrganization);
		},
		onError: () => message.error("Tashkilot holatini o'zgartirib bo'lmadi"),
	});

	const deleteMutation = useMutation({
		mutationFn: () => {
			if (!organizationId) {
				throw new Error("Organization ID is required");
			}
			return organizationsApi.deleteOrganization(organizationId);
		},
		onSuccess: () => {
			message.success("Tashkilot o'chirildi");
			queryClient.invalidateQueries({ queryKey: ["organizations"] });
			navigate("/organizations");
		},
		onError: () => message.error("Tashkilotni o'chirib bo'lmadi"),
	});

	if (isLoading) {
		return (
			<div style={{ padding: 40, textAlign: "center" }}>
				<Spin size="large" />
			</div>
		);
	}

	if (!rawOrganization) {
		return <Navigate replace to="/organizations" />;
	}

	const organization = {
		...rawOrganization,
		createdAt: formatOrganizationDate(rawOrganization.createdAt),
	};

	const kpis = [
		{ title: "Binolar", value: organization.buildingsCount, icon: <BuildOutlined />, tone: "blue" },
		{
			title: "Jami xonalar",
			value: organization.totalRoomsCount,
			icon: <ApartmentOutlined />,
			tone: "sky",
		},
		{
			title: "Sotilgan",
			value: organization.soldRoomsCount,
			icon: <ShoppingCartOutlined />,
			tone: "violet",
		},
		{
			title: "Bo'sh",
			value: organization.availableRoomsCount,
			icon: <HomeOutlined />,
			tone: "green",
		},
		{ title: "Mijozlar", value: organization.clientsCount, icon: <TeamOutlined />, tone: "sky" },
		{
			title: "Jami daromad",
			value: organization.totalRevenue,
			unit: "mlrd",
			icon: <BankOutlined />,
			tone: "green",
		},
		{
			title: "Kutilayotgan",
			value: organization.expectedRevenue,
			unit: "mlrd",
			icon: <ClockCircleOutlined />,
			tone: "amber",
		},
		{
			title: "Foydalanuvchi",
			value: organizationUsers?.items.length ?? organization.usersCount,
			icon: <UserOutlined />,
			tone: "violet",
			onClick: () => navigate(`/users?orgId=${organization.id}`),
		},
	];

	return (
		<div className="crm-organization-detail">
			<div className="crm-page-head">
				<div>
					<OrganizationBackLink />
					<h1>{organization.name}</h1>
					<p>
						kod: {organization.code} · {organization.createdAt} da yaratilgan
					</p>
				</div>
				<div className="crm-detail-actions">
					<OrganizationStatusBadge status={organization.status} />
					<Button
						icon={<EditOutlined />}
						onClick={() => navigate(`/organizations/${organization.id}/edit`)}
					>
						Tahrirlash
					</Button>
					<Popconfirm
						title="Tashkilotni to'xtatish"
						description="Haqiqatan ham ushbu tashkilotni to'xtatmoqchimisiz?"
						onConfirm={() => toggleMutation.mutate()}
						okText="Ha, to'xtatish"
						cancelText="Bekor qilish"
						okButtonProps={{ danger: true }}
					>
						<Button danger loading={toggleMutation.isPending}>
							To'xtatish
						</Button>
					</Popconfirm>
					<Popconfirm
						title="Butunlay o'chirish"
						description="Bu amalni qaytarib bo'lmaydi. Tashkilotni o'chirasizmi?"
						onConfirm={() => deleteMutation.mutate()}
						okText="O'chirish"
						cancelText="Bekor qilish"
						okButtonProps={{ danger: true }}
					>
						<Button
							danger
							icon={<DeleteOutlined />}
							loading={deleteMutation.isPending}
							type="primary"
						>
							O'chirish
						</Button>
					</Popconfirm>
				</div>
			</div>

			<section className="crm-kpi-grid">
				{kpis.slice(0, 4).map((kpi) => (
					<div className="crm-kpi" key={kpi.title}>
						<div className="crm-kpi-title">
							<span className={`crm-kpi-icon ${kpi.tone}`}>{kpi.icon}</span>
							{kpi.title}
						</div>
						<div className="crm-kpi-value">
							<strong>{kpi.value}</strong>
							{"unit" in kpi && kpi.unit ? <span>{kpi.unit}</span> : null}
						</div>
					</div>
				))}
			</section>

			<section className="crm-kpi-grid crm-mt">
				{kpis.slice(4).map((kpi) => (
					<div
						className={`crm-kpi ${kpi.onClick ? "clickable" : ""}`}
						key={kpi.title}
						onClick={kpi.onClick}
						onKeyDown={(event) => {
							if (kpi.onClick && (event.key === "Enter" || event.key === " ")) {
								kpi.onClick();
							}
						}}
						role={kpi.onClick ? "button" : undefined}
						tabIndex={kpi.onClick ? 0 : undefined}
					>
						<div className="crm-kpi-title">
							<span className={`crm-kpi-icon ${kpi.tone}`}>{kpi.icon}</span>
							{kpi.title}
						</div>
						<div className="crm-kpi-value">
							<strong>{kpi.value}</strong>
							{"unit" in kpi && kpi.unit ? <span>{kpi.unit}</span> : null}
						</div>
					</div>
				))}
			</section>
		</div>
	);
}
